"use client";

import { useEffect, useMemo, useState } from "react";
import { compressFileForUpload } from "./file-compression";

type Category = { id: number; name: string; sort_order: number };
type DriveDocument = {
  id: number;
  title: string;
  original_name: string;
  category_id: number;
  category_name: string;
  pharmacies: string;
  content_type: string;
  size_bytes: number;
  uploaded_by: string;
  created_at: number;
};

const pharmacyOptions = ["Square", "Ragadan", "Laurentine"];
const maximumDriveFileSizeMb = 8;
const maximumDriveFileSize = maximumDriveFileSizeMb * 1024 * 1024;

export function DriveModule({ onBack, initialPharmacy, initialOperator }: {
  onBack: () => void;
  initialPharmacy: string;
  initialOperator: string;
}) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [documents, setDocuments] = useState<DriveDocument[]>([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [uploadCategory, setUploadCategory] = useState(0);
  const [newCategory, setNewCategory] = useState("");
  const [pharmacies, setPharmacies] = useState([initialPharmacy || "Square"]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const load = async () => {
    const response = await fetch("/api/drive", { cache: "no-store" });
    const payload = await response.json() as { categories?: Category[]; documents?: DriveDocument[] };
    setCategories(payload.categories ?? []);
    setDocuments(payload.documents ?? []);
    setUploadCategory((current) => current || Number(payload.categories?.[0]?.id ?? 0));
  };

  useEffect(() => { void load(); }, []);

  const visible = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("fr");
    return documents.filter((document) => {
      const matchesCategory = !category || document.category_id === category;
      const matchesQuery = !needle || `${document.title} ${document.original_name} ${document.category_name}`.toLocaleLowerCase("fr").includes(needle);
      return matchesCategory && matchesQuery;
    });
  }, [category, documents, query]);

  const upload = async () => {
    if (!file) return setMessage("Choisissez un fichier.");
    if (!newCategory.trim() && !uploadCategory) return setMessage("Choisissez une catégorie.");
    if (!pharmacies.length) return setMessage("Choisissez au moins une pharmacie.");
    setBusy(true);
    setMessage("");
    try {
      const optimizedFile = await compressFileForUpload(file);
      if (optimizedFile.size > maximumDriveFileSize) throw new Error(`Après compression, ce fichier dépasse encore ${maximumDriveFileSizeMb} Mo.`);
      const form = new FormData();
      form.set("file", optimizedFile);
      form.set("title", title);
      form.set("categoryId", String(uploadCategory));
      form.set("newCategory", newCategory);
      form.set("pharmacies", JSON.stringify(pharmacies));
      form.set("uploadedBy", initialOperator);
      const response = await fetch("/api/drive", { method: "POST", body: form });
      const rawPayload = await response.text();
      let payload: { error?: string } = {};
      try {
        payload = rawPayload ? JSON.parse(rawPayload) as { error?: string } : {};
      } catch {
        if (response.status === 413 || /payload too large/i.test(rawPayload)) {
          throw new Error(`Le fichier est trop volumineux pour l’envoi. Limite : ${maximumDriveFileSizeMb} Mo.`);
        }
        throw new Error("Le serveur n’a pas pu lire ce fichier. Réessayez avec un fichier plus léger.");
      }
      if (!response.ok) throw new Error(payload.error || "Envoi impossible.");
      setFile(null);
      setTitle("");
      setNewCategory("");
      setMessage("Document ajouté au Drive de l’équipe.");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Envoi impossible.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="drive-app">
      <header className="module-header">
        <button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button>
        <div className="drive-brand"><span>🗂️</span><div><strong>Drive équipe</strong><small>Documents partagés · accès rapide</small></div></div>
        <a className="module-tools-shortcut" href="/?tools=1"><span>▦</span><b>Applications</b></a>
      </header>

      <section className="drive-page">
        <header className="drive-hero">
          <div><span>DOCUMENTATION DU RÉSEAU</span><h1>Le bon document,<br />sans chercher partout.</h1><p>Procédures, formulaires, modèles et supports des trois pharmacies.</p></div>
          <aside><strong>{documents.length}</strong><span>documents partagés</span><small>{categories.length} catégories organisées</small></aside>
        </header>

        <section className="drive-search-panel">
          <label><span>⌕</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher un document, une procédure, un mot-clé…" /></label>
        </section>

        <div className="drive-library">
          <aside className="drive-category-panel">
            <header><span>CLASSEMENT</span><h2>Catégories</h2></header>
            <button type="button" className={!category ? "active" : ""} onClick={() => setCategory(0)}><span>✨</span><b>Tous les documents</b><small>{documents.length}</small></button>
            {categories.map((item) => <button type="button" className={category === item.id ? "active" : ""} key={item.id} onClick={() => setCategory(item.id)}><span>📁</span><b>{item.name}</b><small>{documents.filter((document) => document.category_id === item.id).length}</small></button>)}
            <a href="/admin">⚙️ Organiser le Drive</a>
          </aside>

          <section className="drive-results">
            <header><div><span>{category ? categories.find((item) => item.id === category)?.name : "Tous les documents"}</span><h2>{visible.length} document{visible.length > 1 ? "s" : ""}</h2></div><small>PDF, Word, Excel, images et fichiers d’équipe</small></header>
            <div className="drive-grid">
            {visible.length === 0 ? <div className="drive-empty"><span>🗂️</span><strong>Aucun document dans cette vue</strong><p>Modifiez la recherche ou ajoutez un fichier ci-dessous.</p></div> : visible.map((document) => {
              const concerned = (() => { try { return JSON.parse(document.pharmacies) as string[]; } catch { return []; } })();
              const isPdf = document.content_type === "application/pdf";
              return (
                <article key={document.id}>
                  <div className="drive-doc-icon">{isPdf ? "📕" : document.content_type.startsWith("image/") ? "🖼️" : "📄"}</div>
                  <span>{document.category_name}</span>
                  <h3>{document.title}</h3>
                  <p>{concerned.join(" · ") || "Toutes pharmacies"} · ajouté par {document.uploaded_by || "l’équipe"}</p>
                  <small>{Math.max(1, Math.round(document.size_bytes / 1024))} Ko</small>
                  <footer>
                    <a href={`/api/drive?download=${document.id}`}>⬇ Télécharger</a>
                    {isPdf && <a className="print" href={`/api/drive?download=${document.id}&twoUp=1`} target="_blank" rel="noreferrer">🖨️ 2 pages / feuille</a>}
                  </footer>
                </article>
              );
            })}
            </div>
          </section>
        </div>

        <details className="drive-upload">
          <summary><span>＋</span><div><strong>Ajouter un document</strong><small>Déplier uniquement quand vous souhaitez partager un nouveau fichier.</small></div><b>Ouvrir</b></summary>
          <div className="drive-upload-content">
          <header><span>AJOUTER AU DRIVE</span><h2>Partager un document avec l’équipe</h2><p>Choisissez sa catégorie et les pharmacies qui doivent le voir.</p></header>
          <div className="drive-upload-grid">
            <label className="file"><span>Fichier · {maximumDriveFileSizeMb} Mo maximum</span><input type="file" onChange={(event) => {
              const nextFile = event.target.files?.[0] ?? null;
              setFile(nextFile);
              if (!nextFile) return;
              const size = (nextFile.size / 1024 / 1024).toLocaleString("fr-FR", { maximumFractionDigits: 1 });
              setMessage(`${nextFile.name} · ${size} Mo · les photos et PDF seront automatiquement allégés avant l’envoi.`);
            }} /><b>{file?.name || "Choisir ou déposer un fichier"}</b></label>
            <label><span>Titre affiché</span><input value={title} onChange={(event) => setTitle(event.target.value)} placeholder={file?.name.replace(/\.[^.]+$/, "") || "Nom clair pour l’équipe"} /></label>
            <label><span>Catégorie</span><select value={uploadCategory} onChange={(event) => { setUploadCategory(Number(event.target.value)); setNewCategory(""); }}>{categories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
            <label><span>Ou nouvelle catégorie</span><input value={newCategory} onChange={(event) => setNewCategory(event.target.value)} placeholder="Créer une catégorie…" /></label>
          </div>
          <fieldset><legend>Pharmacies concernées</legend>{pharmacyOptions.map((item) => {
            const selected = pharmacies.includes(item);
            return <button type="button" className={selected ? "active" : ""} key={item} onClick={() => setPharmacies((current) => selected ? current.filter((value) => value !== item) : [...current, item])}><i>{selected ? "✓" : ""}</i>{item}</button>;
          })}</fieldset>
          <footer>{message && <p>{message}</p>}<button type="button" disabled={busy} onClick={() => void upload()}>{busy ? "Compression et envoi…" : "☁️ Ajouter au Drive"}</button></footer>
          </div>
        </details>
      </section>
    </main>
  );
}
